import i18n from '@/lang/i18n'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import { store } from '@/store/store'
import AttachmentCountRenderer from '@/cellRenderers/common/AttachmentCountRenderer.vue'
import IncidentRenderer from '@/cellRenderers/common/IncidentRenderer.vue'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import { ICellRendererParams } from '@ag-grid-community/core/dist/cjs/rendering/cellRenderers/iCellRenderer'
import { CellClickedEvent, ColDef } from '@ag-grid-enterprise/all-modules'

export const clinicalGridColumnDefs: (onIdClicked: (event: CellClickedEvent) => void, clientSide: boolean) => (ColDef | ExportedColDef | ExportedColGroupDef)[] =
    (onIdClicked, clientSide = false) => ([
        { type: AgGridColumnType.CUSTOM_CHECKBOX },
        {
            headerName: i18n.tc('global.request'),
            children: [
                {
                    headerName: i18n.tc('grid.headerName.requestLabel'),
                    field: 'label',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    width: 110,
                    suppressSizeToFit: true,
                    onCellClicked: onIdClicked
                }, {
                    headerName: i18n.tc('global.status'),
                    field: 'status',
                    type: AgGridColumnType.COMMON_REQUEST_STATUS,
                    exportField: 'status.name',
                    exportType: AgGridExportType.TEXT,
                    suppressMenu: false
                }, {
                    headerName: i18n.tc('grid.reqRequestDate'),
                    colId: 'reqRequestDate',
                    field: 'requestDate',
                    type: [AgGridColumnType.DATETIME, 'fixedWidth'],
                    exportType: AgGridExportType.DATETIME
                }, {
                    headerName: i18n.tc('global.priority'),
                    colId: 'priority.level',
                    field: 'priority',
                    type: AgGridColumnType.PRIORITY,
                    exportField: 'priority.name',
                    exportType: AgGridExportType.TEXT
                }, {
                    headerName: i18n.tc('global.episode'),
                    colId: 'reqEpisode',
                    field: 'episode',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    minWidth: 110,
                    suppressSizeToFit: true
                }, {
                    headerName: i18n.tc('registry.cases.professional'),
                    colId: clientSide ? 'physicianPetitioner.fullName' : 'physicianPetitioner.normalizedFullName',
                    field: 'physicianPetitioner.fullName',
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    exportType: AgGridExportType.TEXT,
                    suppressSizeToFit: true
                }, {
                    headerName: i18n.tc('registry.cases.unit'),
                    field: 'physicianDepartment.name',
                    type: AgGridColumnType.DEPARTMENTS,
                    exportType: AgGridExportType.TEXT,
                    suppressSizeToFit: true
                }, {
                    headerName: i18n.tc('global.center'),
                    field: 'physicianDepartment.center.name',
                    type: AgGridColumnType.CENTERS,
                    exportType: AgGridExportType.TEXT,
                    suppressSizeToFit: true
                }, {
                    headerName: i18n.tc('global.externalReference'),
                    field: 'externalReference',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    suppressSizeToFit: true
                }
            ]
        }, {
            headerName: i18n.tc('global.case'),
            children: [
                {
                    headerName: i18n.tc('grid.casCasenumber'),
                    field: 'caseStudy.casCasenumber',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    suppressSizeToFit: true
                }, {
                    headerName: i18n.tc('grid.casRegistrationDate'),
                    field: 'caseStudy.casRegistrationDate',
                    type: [AgGridColumnType.DATETIME, 'fixedWidth'],
                    exportType: AgGridExportType.DATETIME
                }, {
                    headerName: i18n.tc('grid.casValidationDate'),
                    field: 'caseStudy.casValidationDate',
                    type: [AgGridColumnType.DATETIME, 'fixedWidth'],
                    exportType: AgGridExportType.DATETIME
                }, {
                    headerName: i18n.tc('grid.labName'),
                    field: 'laboratory.labName',
                    type: AgGridColumnType.LABORATORIES,
                    exportType: AgGridExportType.TEXT,
                    suppressSizeToFit: true
                }
            ]
        }, {
            headerName: i18n.tc('global.patient'),
            children: [
                {
                    headerValueGetter: () => store.getters.parameters.global?.mainPatientIdentifierType.name,
                    field: 'patient.mainPatientIdentifier.paiIdentifier',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    filter: false,
                    filterParams: {
                        filterOptions: ['equals'],
                        defaultOption: 'equals',
                        newRowsAction: 'keep'
                    },
                    sortable: false,
                    suppressSizeToFit: true
                }, {
                    headerValueGetter: () => store.getters.parameters.global?.secondaryPatientIdentifierType.name,
                    field: 'patient.secondaryPatientIdentifier.paiIdentifier',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    filter: false,
                    filterParams: {
                        filterOptions: ['equals'],
                        defaultOption: 'equals',
                        newRowsAction: 'keep'
                    },
                    sortable: false,
                    suppressSizeToFit: true
                }, {
                    headerName: i18n.tc('grid.patName'),
                    colId: 'patient.patName',
                    field: 'patient.name',
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    exportType: AgGridExportType.TEXT,
                    suppressSizeToFit: true
                }, {
                    headerName: i18n.tc('grid.patMiddleName'),
                    colId: 'patient.patMiddleName',
                    field: 'patient.middleName',
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    exportType: AgGridExportType.TEXT,
                    suppressSizeToFit: true
                }, {
                    headerName: i18n.tc('grid.patSurname1'),
                    colId: 'patient.patSurname1',
                    field: 'patient.surname1',
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    exportType: AgGridExportType.TEXT,
                    suppressSizeToFit: true
                }, {
                    headerName: i18n.tc('grid.patSurname2'),
                    colId: 'patient.patSurname2',
                    field: 'patient.surname2',
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    exportType: AgGridExportType.TEXT,
                    suppressSizeToFit: true
                }
            ]
        },
        {
            headerName: i18n.tc('grid.hasAlarms'),
            headerTooltip: i18n.tc('grid.hasAlarms'),
            headerComponentParams: {
                template: buildIconHeaderTemplate('far fa-exclamation-triangle')
            },
            field: 'incidents',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.INTEGER,
            hide: true,
            filter: false,
            suppressSizeToFit: true,
            cellRendererFramework: IncidentRenderer
        },
        {
            headerName: i18n.tc('grid.attachments'),
            headerTooltip: i18n.tc('grid.attachments'),
            headerComponentParams: {
                template: buildIconHeaderTemplate('far fa-paperclip')
            },
            cellRendererFramework: AttachmentCountRenderer,
            field: 'attachments',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.INTEGER,
            hide: false,
            filter: false,
            suppressSizeToFit: true
        },
        {
            headerName: i18n.tc('global.automaticCreation'),
            headerTooltip: i18n.tc('global.automaticCreation'),
            headerComponentParams: {
                template: buildIconHeaderTemplate('far fa-magic')
            },
            cellRenderer: ({ value }: ICellRendererParams) => value === true ? '<i class="success far fa-check"/>' : '',
            field: 'automaticallyCreated',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.BOOLEAN,
            hide: false,
            filter: false
        }
    ])
